import './integration-type-selector.scss';
import Card from '../../../../../components/layouts/card/card';
import { observer } from 'mobx-react';
import { FormFieldLine } from '../../../../../components/form-field-line/form-field-line';
import { ButtonIcon } from '../../../../../components/inputs/button-icon/button-icon';
import IconApply from '../../../../../components/icons/icon-apply/icon-apply';
import IconDiscard from '../../../../../components/icons/icon-discard/icon-discard';
import { IOption, SelectDropdownGrouped } from '../../../../../components/inputs/select-dropdown-grouped/select-dropdown-grouped';
import { useState } from 'react';
import { SingleValue } from 'react-select';
import { IIntegrationPreset, ISelectedIntegration } from '../../../../../models/IntegrationsTypes';
import integrationListStore from '../../stores/integrations-list-store';

interface IIntegrationTypeSelectorProps {
    toggleNewIntegration: () => void;
}

const IntegrationTypeSelector: React.FC<IIntegrationTypeSelectorProps> = observer(({toggleNewIntegration}) => {
    const [selectedIntegration, setSelectedIntegration] = useState<ISelectedIntegration|undefined>();

    const onIntegrationSelection = (e: SingleValue<IOption>) => {
        setSelectedIntegration(e as ISelectedIntegration);
    };

    const onTypeSelected = () => {
        if (!selectedIntegration) {
            return;
        }
        integrationListStore.addIntegration(selectedIntegration);
        toggleNewIntegration();
    };

    const isApplyDisabled = () => {
        if (!selectedIntegration?.isAllowed) {
            return true;
        }

        return false;
    }

    const note = (): { title: string, description: string, isHtml: boolean }|undefined => {
        if (!selectedIntegration) {
            return undefined;
        }

        /** happens when this feature is disabled for user */
        if (!selectedIntegration.isAllowed) {
            return {
                title: 'Warning',
                description: 'Sorry, this feature is not included in your subscription plan.',
                isHtml: true,
            };
        }

        const preset = integrationListStore.findIntegrationPreset(selectedIntegration.category, selectedIntegration.value);

        if (preset.description) {
            return {
                title: 'About the solution',
                description: preset.description,
                isHtml: true,
            };
        }

        return undefined;
    }

    return (
        <Card
            toolbar={{ 
                title:'New Integration',
                content: (
                    <div className='integration-type-selector-buttons'>
                        <ButtonIcon Component={IconDiscard} onClick={toggleNewIntegration}/>
                        <ButtonIcon Component={IconApply} disabled={isApplyDisabled()} onClick={onTypeSelected} />
                    </div>
                )
            }}
            note={note()}
            isExpandable={false}
            isScrollToViewAtMount
        >
            <FormFieldLine
                label="Integrate with"
                note={
                    <span>Can’t find a product or service you need to integrate with your account? Submit a request to <a href="mailto:support@returngo.ai">support@returngo.ai</a></span>
                }
                required
            >
                <SelectDropdownGrouped 
                    options={integrationListStore.integrationPresets.map(preset => ({
                        category: preset.label,
                        options: preset.integrations.map((value: IIntegrationPreset) => ({
                            value: value.id,
                            label: value.label,
                            isDisabled: value.available_soon,
                            category: preset.id,
                            isAllowed: value.exclude_from_feature_category || preset.is_allowed,
                        })),
                    }))}
                    onChange={onIntegrationSelection}
                />
            </FormFieldLine>
        </Card>
    );
});

export default IntegrationTypeSelector;
